import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  useDataSources,
  useCreateDataSource,
  useUpdateDataSource,
  useDeleteDataSourceItem,
} from '@/hooks/useDataSources';
import {
  DATA_SOURCE_TYPE_CODES,
  NETWORK_CODES,
  type DataSource,
  type GroupedDataSource,
  type CreateDataSourceRequest,
  type UpdateDataSourceRequest,
} from '@/types/dataSources';
import { dataSourcesService } from '@/services/dataSourcesService';

/**
 * Hook to fetch list of main channels using unified datasources API
 * Returns flattened DataSource[] from the unified endpoint
 */
export const useMainChannels = (
  filters?: { account_id?: number; is_active?: boolean; search?: string },
  options?: any
) => {
  return useDataSources(
    {
      account_id: filters?.account_id,
      type: 'main_channel',
      is_active: filters?.is_active,
      search: filters?.search,
    },
    {
      enabled: options?.enabled !== false && !!filters?.account_id,
      select: (response: any) => response?.data || [],
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      ...options,
    }
  );
};

/**
 * Hook to fetch list of main channels grouped by datasource
 * Returns GroupedDataSource[] (datasources with items grouped)
 */
export const useMainChannelsGrouped = (
  filters?: { account_id?: number; is_active?: boolean; search?: string },
  options?: Omit<UseQueryOptions<GroupedDataSource[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<GroupedDataSource[]>({
    queryKey: ['datasources', 'main_channel', filters],
    queryFn: async () => {
      const response = await dataSourcesService.getListGrouped({
        account_id: filters?.account_id,
        type: 'main_channel',
        is_active: filters?.is_active,
        search: filters?.search,
      });
      return response.data;
    },
    enabled: options?.enabled !== false && !!filters?.account_id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to create a new main channel using unified API
 */
export const useCreateMainChannel = () => {
  const queryClient = useQueryClient();
  const createDataSourceMutation = useCreateDataSource();

  return useMutation({
    mutationFn: async (data: {
      account_id: number;
      author_username?: string;
      author_id?: number;
      is_active?: boolean;
      analysis_since?: string;
    }) => {
      // Convert to unified API format
      const createRequest: CreateDataSourceRequest = {
        account_id: data.account_id,
        name: data.author_username || 'Main Channel',
        data_source_type: DATA_SOURCE_TYPE_CODES.main_channel,
        items: [
          {
            author_username: data.author_username,
            author_id: data.author_id,
            network_id: NETWORK_CODES.instagram, // Default to Instagram
            dates: [
              {
                start_date: data.analysis_since || new Date().toISOString().split('T')[0],
                status: data.is_active === false ? 1 : 0,
              },
            ],
          },
        ],
      };

      return await createDataSourceMutation.mutateAsync(createRequest);
    },
    onSuccess: () => {
      // Invalidate only main_channel queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'main_channel'] });
    },
    onError: (error) => {
      console.error('[useCreateMainChannel] Mutation error:', error);
    },
  });
};

/**
 * Hook to update an existing main channel using unified API
 */
export const useUpdateMainChannel = () => {
  const queryClient = useQueryClient();
  const updateDataSourceMutation = useUpdateDataSource();

  return useMutation({
    mutationFn: async ({
      id,
      datasource_id,
      data,
    }: {
      id: number;
      datasource_id?: number;
      data: { is_active: boolean; analysis_since?: string; analysis_end?: string };
    }) => {
      if (!datasource_id) {
        throw new Error('datasource_id is required for update');
      }

      // Fetch current datasource once to get existing dates
      const currentDatasource = await dataSourcesService.getDataSourceRaw(datasource_id);
      const currentItem = currentDatasource.items.find(item => item.id === id);

      if (!currentItem) {
        throw new Error('Item not found in datasource');
      }

      const existingDates = currentItem.dates || [];

      // Update dates[0] or create new one if doesn't exist
      const updatedDates: Array<any> = [];

      if (existingDates.length > 0) {
        // Update first date - include id for existing date
        updatedDates.push({
          id: existingDates[0].id,
          start_date: data.analysis_since || existingDates[0].start_date,
          end_date: data.analysis_end !== undefined ? data.analysis_end : existingDates[0].end_date,
          status: existingDates[0].status,
        });

        // Preserve remaining dates with their ids
        for (let i = 1; i < existingDates.length; i++) {
          updatedDates.push({
            id: existingDates[i].id,
            start_date: existingDates[i].start_date,
            end_date: existingDates[i].end_date,
            status: existingDates[i].status,
          });
        }
      } else {
        // Create first date - no id for new date
        updatedDates.push({
          start_date: data.analysis_since || new Date().toISOString().split('T')[0],
          end_date: data.analysis_end || null,
          status: 0,
        });
      }

      const updateRequest: UpdateDataSourceRequest = {
        items: [
          {
            id,
            dates: updatedDates,
          },
        ],
      };

      return await updateDataSourceMutation.mutateAsync({
        datasourceId: datasource_id,
        data: updateRequest,
      });
    },
    onSuccess: () => {
      // Invalidate only main_channel queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'main_channel'] });
    },
    onError: (error) => {
      console.error('[useUpdateMainChannel] Mutation error:', error);
    },
  });
};

/**
 * Hook to update main channel datasource (multi-platform support)
 */
export const useUpdateMainChannelDatasource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      datasourceId,
      data,
    }: {
      datasourceId: number;
      data: UpdateDataSourceRequest;
    }) => {
      return await dataSourcesService.updateDataSource(datasourceId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasources', 'main_channel'] });
    },
    onError: (error) => {
      console.error('[useUpdateMainChannelDatasource] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete a main channel item (single platform)
 */
export const useDeleteMainChannel = () => {
  const queryClient = useQueryClient();
  const deleteDataSourceItemMutation = useDeleteDataSourceItem();

  return useMutation({
    mutationFn: async (params: {
      id: number;
      datasource_id: number;
    }) => {
      return await deleteDataSourceItemMutation.mutateAsync({
        datasourceId: params.datasource_id,
        itemId: params.id,
      });
    },
    onSuccess: () => {
      // Invalidate only main_channel queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'main_channel'] });
    },
    onError: (error) => {
      console.error('[useDeleteMainChannel] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete entire main channel datasource (all platforms)
 */
export const useDeleteMainChannelDatasource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (datasourceId: number) => {
      return await dataSourcesService.deleteDataSource(datasourceId);
    },
    onSuccess: () => {
      // Invalidate only main_channel queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'main_channel'] });
    },
    onError: (error) => {
      console.error('[useDeleteMainChannelDatasource] Mutation error:', error);
    },
  });
};
